import { useEffect, useState } from 'react';
import {
  DomainRenewalBlacklist,
  ListDomainRenewalBlacklistResponse,
} from '../api/models';
import { Form, Input, Modal, Select, notification } from 'antd';
import APIClient from '../api/api';
import moment from 'moment';
import { debounce } from 'lodash';
import { useForm } from 'antd/es/form/Form';
import DomainFilterBox from '../components/DomainFilterBox';
import {
  Button,
  Column,
  DataTable,
  DomainName,
  FilterPill,
  Pagination,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const PAGE_SIZE = 25;

interface UploadModalProps {
  onClose: (updated: boolean) => void;
}

const UploadModal = (props: UploadModalProps) => {
  const [form] = useForm();
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File>();

  async function onOk() {
    form.submit();
  }

  async function finish(values: any) {
    try {
      setUploading(true);
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (values.updateType === 'add') {
        await APIClient.batchAddDomainRenewalBlacklist(formData);
      } else if (values.updateType === 'delete') {
        await APIClient.batchDeleteDomainRenewalBlacklist(formData);
      }

      notification.success({ message: 'Batch update complete.' });
      props.onClose(true);
    } catch (e) {
      notification.error({ message: 'Failed to upload file.' });
    } finally {
      setUploading(false);
    }
  }

  function onSetFile(e: any) {
    setFile(e.target.files[0]);
  }

  return (
    <Modal
      title="Batch Update Blacklist"
      open
      onCancel={() => props.onClose(false)}
      onOk={onOk}
      confirmLoading={uploading}
    >
      <Form form={form} onFinish={finish}>
        <Form.Item
          name="updateType"
          rules={[{ required: true, message: 'Update Type is required' }]}
        >
          <Select placeholder="Type of Update">
            <Select.Option key="add">Add</Select.Option>
            <Select.Option key="delete">Delete</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="file" rules={[{ required: true, message: 'File is required' }]}>
          <Input type="file" onChange={onSetFile} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const DomainRenewalBlacklistPage = () => {
  const [data, setData] = useState<ListDomainRenewalBlacklistResponse>();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [domainsFilter, setDomainsFilter] = useState<string[]>();
  const [search, setSearch] = useState<string>('');
  const [showDomainsFilter, setShowDomainsFilter] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, domainsFilter]);

  async function loadData(p: number) {
    setLoading(true);
    try {
      const resp = await APIClient.listDomainRenewalBlacklist(
        { page: p, pageSize: PAGE_SIZE },
        { search: search || undefined, domains: domainsFilter },
      );
      setData(resp);
      setPage(resp.currentPage || p);
    } catch (e) {
      notification.error({ message: 'Failed to load blacklist.' });
    } finally {
      setLoading(false);
    }
  }

  const debouncedSearch = debounce((value: string) => setSearch(value), 400);

  const columns: Column<DomainRenewalBlacklist>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (d) => <DomainName name={d.domain} />,
    },
    {
      key: 'addedOn',
      title: 'Added On',
      width: 180,
      render: (d) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {moment(d.addedOn).format('MMM D, YYYY')}
        </span>
      ),
    },
  ];

  function onBatchUpdateFinished(updated: boolean) {
    if (updated) loadData(page);
    setShowUploadModal(false);
  }

  return (
    <>
      <PageHead
        title="Domain Renewal Blacklist"
        subtitle="Domains excluded from automated renewal logic."
        actions={
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            Bulk Update
          </Button>
        }
      />
      <PageToolbar>
        <input
          type="text"
          defaultValue={search}
          placeholder="Search domain…"
          onChange={(e) => debouncedSearch(e.target.value)}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[280px] text-[12.5px] text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />
        <FilterPill
          label="Domains"
          value={domainsFilter?.length ? `${domainsFilter.length}` : 'Any'}
          active={!!domainsFilter?.length}
          onClick={() => setShowDomainsFilter((s) => !s)}
        />
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {data ? (
            <>
              total <b className="text-text-2">{data.total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      {showDomainsFilter && (
        <div className="px-6 py-3 border-b border-border bg-bg">
          <DomainFilterBox loading={!data} onChange={(d) => setDomainsFilter(d)} />
        </div>
      )}

      <DataTable
        columns={columns}
        data={data?.content ?? []}
        rowKey={(d) => d.domain}
        loading={loading}
        emptyMessage="No blacklisted domains."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{data?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{data?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data?.total ?? 0}
            onPageChange={loadData}
            loading={loading}
          />
        }
      />

      {showUploadModal && <UploadModal onClose={onBatchUpdateFinished} />}
    </>
  );
};

export default DomainRenewalBlacklistPage;
